import { create } from 'zustand'

import {
  snapshotEngine,
  type QuoteSnapshot,
  type SnapshotCompareResult,
  type SnapshotHistoryEntry,
  type SnapshotPermissions,
  type SnapshotSearchFilters,
} from '@/services/snapshot'

interface SnapshotStoreState {
  snapshots: QuoteSnapshot[]
  selectedQuoteId: string | null
  selectedSnapshotId: string | null
  compareLeftId: string | null
  compareRightId: string | null
  compareResult: SnapshotCompareResult | null
  history: SnapshotHistoryEntry[]
  permissions: SnapshotPermissions
  searchFilters: SnapshotSearchFilters
  timelineOpen: boolean
  compareOpen: boolean

  hydrateForQuote: (quoteId: string) => void
  setSelectedSnapshot: (id: string | null) => void
  setTimelineOpen: (open: boolean) => void
  setCompareOpen: (open: boolean) => void
  setComparePair: (leftId: string | null, rightId: string | null) => void
  runCompare: () => void
  setSearchFilters: (filters: Partial<SnapshotSearchFilters>) => void
  setPermissions: (permissions: Partial<SnapshotPermissions>) => void
  refresh: () => void
}

export const useSnapshotStore = create<SnapshotStoreState>((set, get) => ({
  snapshots: [],
  selectedQuoteId: null,
  selectedSnapshotId: null,
  compareLeftId: null,
  compareRightId: null,
  compareResult: null,
  history: [],
  permissions: snapshotEngine.getPermissions(),
  searchFilters: {},
  timelineOpen: false,
  compareOpen: false,

  hydrateForQuote: (quoteId) => {
    const snapshots = snapshotEngine.listByQuote(quoteId)
    const latest = snapshots[0] ?? null
    set({
      selectedQuoteId: quoteId,
      snapshots,
      selectedSnapshotId: latest?.id ?? null,
      history: latest ? snapshotEngine.listHistory(latest.id) : [],
      compareLeftId: snapshots[1]?.id ?? latest?.id ?? null,
      compareRightId: latest?.id ?? null,
    })
  },

  setSelectedSnapshot: (id) => {
    set({
      selectedSnapshotId: id,
      history: id ? snapshotEngine.listHistory(id) : [],
    })
  },

  setTimelineOpen: (open) => set({ timelineOpen: open }),
  setCompareOpen: (open) => set({ compareOpen: open }),

  setComparePair: (leftId, rightId) => set({ compareLeftId: leftId, compareRightId: rightId }),

  runCompare: () => {
    const { compareLeftId, compareRightId } = get()
    if (!compareLeftId || !compareRightId) {
      set({ compareResult: null })
      return
    }
    set({ compareResult: snapshotEngine.compare(compareLeftId, compareRightId) })
  },

  setSearchFilters: (filters) =>
    set((state) => ({
      searchFilters: { ...state.searchFilters, ...filters },
      snapshots: snapshotEngine.search({
        ...state.searchFilters,
        ...filters,
        quoteId: state.selectedQuoteId,
      }),
    })),

  setPermissions: (permissions) => {
    const next = snapshotEngine.setPermissions(permissions)
    set({ permissions: next })
  },

  refresh: () => {
    const quoteId = get().selectedQuoteId
    if (!quoteId) return
    get().hydrateForQuote(quoteId)
  },
}))
